import React, { useEffect, useRef, useState } from "react";

/**
 * One location, as a card. The results layout at every width — columns are
 * handled in CSS (see .il-image-search-cards).
 *
 * The old desktop grid showed a group's extra images by expanding them INLINE
 * as siblings, which is what made the mobile layout confusing: only the cover
 * carried a label, so on a narrow screen the members stacked underneath and
 * read as belonging to whatever location's name sat above them.
 *
 * Here containment is structural rather than a labelling convention — the extra
 * images live in a scroller INSIDE this card, so an image physically cannot end
 * up under another location's name. There is deliberately no expand/collapse:
 * the strip is always present when there is more than one match, so the
 * "+N more" toggle has no job on mobile.
 */
export default function LocationCard({ group, onCopyLink, onFindSimilar, strongMatch }) {
    const cover = group.cover;
    const extras = group.images.slice(1);
    const locationHref = "/" + group.slug;

    // The strip scrolls with overflow-x, which a trackpad or a finger can
    // drive but a plain mouse cannot — there is no horizontal wheel gesture,
    // and macOS hides the scrollbar until you are already scrolling. These
    // arrows are the mouse's way in. They render only for the direction that
    // has somewhere to go, so their presence doubles as the "more images this
    // way" signal; CSS hides them entirely on touch devices.
    const stripRef = useRef(null);
    const [canScroll, setCanScroll] = useState({ left: false, right: false });

    const measureStrip = () => {
        const el = stripRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        const left = el.scrollLeft > 1;
        const right = el.scrollLeft < max - 1;
        // Bail out when nothing changed — this runs on every scroll event, and
        // a fresh object every time would re-render the card continuously
        // while the smooth scroll animates.
        setCanScroll((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };

    useEffect(() => {
        measureStrip();
        // Card width follows the window, so whether the strip overflows does too.
        window.addEventListener("resize", measureStrip);
        return () => window.removeEventListener("resize", measureStrip);
    }, [group]);

    const pageStrip = (dir) => {
        const el = stripRef.current;
        if (!el) return;
        // Most of a viewport per click, keeping a sliver of the previous page
        // in view so the reader can tell the strip moved rather than jumped.
        el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
    };

    // Mouse drag pans the strip the way a finger does. The strip is made of
    // links, so the drag and the click have to be told apart: within a 5px
    // threshold the press is a click and passes through untouched; beyond it
    // the press is a drag, and the click that fires on release is swallowed
    // in the capture phase before it can reach a link. Mouse only — touch
    // already pans natively, and capturing it here would break that.
    // A ref, not state: drag position changes on every mousemove and nothing
    // needs to re-render for it.
    const dragRef = useRef({ active: false, moved: false, startX: 0, startScroll: 0 });

    const onStripPointerDown = (e) => {
        if (e.pointerType !== "mouse" || e.button !== 0) return;
        dragRef.current = {
            active: true,
            moved: false,
            startX: e.clientX,
            startScroll: stripRef.current.scrollLeft,
        };
        // The release can land anywhere — another element, past the window
        // edge — and pointer capture is not guaranteed to route it back here.
        // A missed release left the drag armed, so the strip panned again the
        // moment the cursor wandered back in, with no button held. Listen at
        // the window so the drag ends wherever the button comes up. once:true
        // makes a listener that outlives its drag harmless: it fires on some
        // later release, finds the drag already over, and removes itself.
        window.addEventListener("pointerup", onStripPointerEnd, { once: true });
        window.addEventListener("pointercancel", onStripPointerEnd, { once: true });
    };

    const onStripPointerMove = (e) => {
        const d = dragRef.current;
        const el = stripRef.current;
        if (!d.active || !el) return;
        // Belt to the window listener's braces: if the button is no longer
        // down, the release happened somewhere no listener could hear it.
        // Recover instead of panning without a pressed button.
        if ((e.buttons & 1) === 0) {
            onStripPointerEnd();
            return;
        }
        const dx = e.clientX - d.startX;
        if (!d.moved) {
            if (Math.abs(dx) < 5) return;
            d.moved = true;
            // Capture only once it IS a drag, so the pointer keeps driving the
            // strip even when it strays outside — and plain clicks never see
            // any capture at all. Best-effort: the recovery paths above cover
            // a capture that fails or gets lost.
            try {
                el.setPointerCapture(e.pointerId);
            } catch (err) {
                /* stale pointerId — recovery paths handle it */
            }
            el.style.cursor = "grabbing";
        }
        el.scrollLeft = d.startScroll - dx;
    };

    const onStripPointerEnd = () => {
        dragRef.current.active = false;
        if (stripRef.current) stripRef.current.style.cursor = "";
        // The click that follows an in-strip drag fires synchronously after
        // pointerup, so it still sees moved=true and gets swallowed. Clearing
        // on a timeout is for the drag that ends OUTSIDE the strip: no click
        // follows, and without this reset the flag would lie in wait and
        // swallow the next genuine click.
        setTimeout(() => {
            dragRef.current.moved = false;
        }, 0);
    };

    const onStripClickCapture = (e) => {
        if (!dragRef.current.moved) return;
        dragRef.current.moved = false;
        e.preventDefault();
        e.stopPropagation();
    };

    const onStripDragStart = (e) => {
        // The browser's native image/link drag would wrestle the pan for the
        // same gesture. Suppressed only while a press is live, so images stay
        // draggable out of the page when the strip is at rest.
        if (dragRef.current.active) e.preventDefault();
    };

    return (
        <div className="il-location-card">
            <div className="il-location-card-cover position-relative">
                <a
                    className="d-block"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={locationHref + "?img=" + cover._id}
                >
                    <img
                        className="w-100 object-contain"
                        loading="lazy"
                        /* _display is an xs conversion; _url is the full-size
                           original and is what share still uses. */
                        src={cover._display || cover._url}
                        alt={group.location}
                    />
                </a>

                {strongMatch && (
                    /* Carried over from the grid deliberately. It says something
                       about result quality, and it matters MORE here — fewer
                       results are visible at once on a phone. */
                    <span className="il-strong-match position-absolute">Strong match</span>
                )}

                <button
                    type="button"
                    onClick={(e) => onCopyLink(e, group.slug, cover._id)}
                    className="position-absolute il-share-image-btn il-share-ready"
                    title="Copy image link"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                        strokeLinejoin="round" width="14" height="14">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                </button>

                {onFindSimilar && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onFindSimilar(cover);
                        }}
                        title={"Find locations similar to this photo of " + group.location}
                        className="il-find-similar-btn position-absolute d-flex align-items-center">
                        <span className="icon-container bg-primary d-flex justify-content-center align-items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="search-icon" width="11" height="11">
                                <path fill="currentColor" d="M508.5 468.9L387.1 347.5c-2.3-2.3-5.3-3.5-8.5-3.5h-13.2c31.5-36.5 50.6-84 50.6-136C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c52 0 99.5-19.1 136-50.6v13.2c0 3.2 1.3 6.2 3.5 8.5l121.4 121.4c4.7 4.7 12.3 4.7 17 0l22.6-22.6c4.7-4.7 4.7-12.3 0-17z" />
                            </svg>
                        </span>
                        <span className="ml-1">Find similar</span>
                    </button>
                )}
            </div>

            {extras.length > 0 && (
                /* The wrapper exists so the arrows can sit OVER the strip
                   without scrolling with it. */
                <div className="il-location-card-strip-wrap position-relative">
                    <div
                        className="il-location-card-strip"
                        ref={stripRef}
                        onScroll={measureStrip}
                        onPointerDown={onStripPointerDown}
                        onPointerMove={onStripPointerMove}
                        onPointerUp={onStripPointerEnd}
                        onPointerCancel={onStripPointerEnd}
                        onClickCapture={onStripClickCapture}
                        onDragStart={onStripDragStart}
                    >
                        {extras.map((img) => (
                            <a
                                key={img._id}
                                className="il-location-card-strip-item"
                                target="_blank"
                                rel="noopener noreferrer"
                                href={locationHref + "?img=" + img._id}
                            >
                                <img
                                    className="object-contain"
                                    loading="lazy"
                                    src={img._display || img._url}
                                    alt={group.location}
                                />
                            </a>
                        ))}
                    </div>

                    {canScroll.left && (
                        <button
                            type="button"
                            className="il-strip-arrow il-strip-arrow-left"
                            aria-label="Scroll images left"
                            onClick={() => pageStrip(-1)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"
                                fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                    )}
                    {canScroll.right && (
                        <button
                            type="button"
                            className="il-strip-arrow il-strip-arrow-right"
                            aria-label="Scroll images right"
                            onClick={() => pageStrip(1)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16"
                                fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </button>
                    )}
                </div>
            )}

            {/* Below ALL of the location's images, so the photos read as one
                block and the caption belongs to the block rather than splitting
                it. The match count folds in here rather than sitting as its own
                line under the strip — two stacked text blocks at the foot of a
                card is more furniture than the information warrants. */}
            <div className="il-location-card-label">
                <h5 className="location-name font-weight-normal truncate mb-0">
                    <a className="text-body" target="_blank" rel="noopener noreferrer"
                        href={locationHref} title={group.location}>
                        {group.location}
                    </a>
                </h5>
                {cover.city && (
                    <p className="location-city ellipse-overflow h6 mb-0">
                        <span className="text-dark h6 small">{cover.city}</span>
                    </p>
                )}
                {cover.permit && (
                    <p className="location-permit ellipse-overflow h6 mb-0">
                        <span className="text-muted h6 small">{cover.permit}</span>
                    </p>
                )}
                {extras.length > 0 && (
                    <p className="il-location-card-strip-label mb-0">
                        {group.matchCount} matching images
                    </p>
                )}
            </div>
        </div>
    );
}
